'use client';

/** SDL text input with size and state variants. Style variants: "default" (optional left/right addons) and "floating" (pill with an animated label). */

import * as React from 'react';
import { coerceString } from '@federa/const/string-guards';
import { useFormFieldState } from '../primitives/hooks/useFormFieldState.ts';
import { useFormFieldStyles, getFormFieldFocusRingStyles } from '../primitives/hooks/useFormFieldStyles.ts';
import { buildInputStyles, leftAddonStyles, rightAddonStyles } from '../primitives/Input.styles.ts';
import {
	BARE_BOX_OVERRIDES,
	deriveInputMessage,
	deriveInputState,
	inputSizeStyles,
	inputStateStyles,
	pickerWrapperStyles,
} from '../primitives/input-helpers.ts';
import { RequiredIndicator, ContentInputCore } from '../primitives/input-components.tsx';
import { ContentFieldFooter } from './FieldFooter.tsx';
import { FloatingInputContent } from './InputFloating.tsx';
import type { ComponentSize } from '@federa/types/ui-design-tokens';
import type { FormFieldState } from '@federa/types/ui-form-field';
import type { InputProps, DefaultInputContentProps } from '@federa/types/ui-input';

// The bag is bound to a NAMED parameter and destructured in the body rather than in the signature, because the shared
// members now travel to `ContentInputCore` / `ContentFieldFooter` as one object: destructuring in the signature is
// what forced both variants to re-spell those forwards prop by prop. The parameter is `content`, not `props` -- the
// contract carries a member of its own by that name (the native-attribute rest).
function DefaultInputContent(content: DefaultInputContentProps): React.ReactElement {
	const {
		inputId,
		label,
		size,
		stateStyle,
		isFocused,
		disabled,
		bare,
		leftAddon,
		rightAddon,
		wrapperClassName,
		style,
		containerStyle,
		required,
		formFieldStyles,
		fullWidth,
	} = content;
	const sizeStyle = inputSizeStyles[size];
	const baseInputStyles = buildInputStyles({
		sizeStyle,
		stateStyle,
		isFocused,
		disabled,
		leftAddon,
		rightAddon,
	});
	const focusRingStyles = getFormFieldFocusRingStyles(isFocused);

	return (
		// A date or time box is as wide as its value, not the row (`pickerWrapperStyles`).
		<div style={{ ...formFieldStyles.wrapperStyles, ...pickerWrapperStyles(content.props.type, fullWidth) }} className={wrapperClassName}>
			{label && (
				<label htmlFor={inputId} style={formFieldStyles.labelStyles}>
					{label}
					{required && <RequiredIndicator />}
				</label>
			)}
			{/* `containerStyle` LAST: this div is the box the user sees, so a call site nesting the control inside its own
			    surface must be able to own it. Cancelling `border` through `style` only reaches the `<input>`, which left
			    this container's box drawn INSIDE the caller's with no prop to stop it. */}
			<div style={{ ...formFieldStyles.containerStyles, ...containerStyle }}>
				{leftAddon && <span style={leftAddonStyles}>{leftAddon}</span>}
				<ContentInputCore
					content={content}
					// `bare` lands AFTER the focus ring and BEFORE the caller's `style`: the ring is `boxShadow` and
					// survives the reset by construction, while a call site that wants one box member back must still win.
					inputStyle={{ ...baseInputStyles, ...focusRingStyles, ...(bare ? BARE_BOX_OVERRIDES : {}), ...style }}
				/>
				{rightAddon && <span style={rightAddonStyles}>{rightAddon}</span>}
			</div>
			<ContentFieldFooter content={content} messageStyles={formFieldStyles.helperTextStyles} />
		</div>
	);
}

/**
 * Shared setup hook for both input variants.
 *
 * IT NOW RUNS THE SAME DERIVATION EVERY OTHER SDL CONTROL RUNS. This used to read
 * `state === 'error' && errorMessage ? errorMessage : helperText`, which is a THIRD spelling of the field-validity
 * decision beside `Textarea`'s `!!error` and `CurrencyInput`'s `deriveInputState`, and it had a hole the other two did
 * not: an `<Input errorMessage="...">` with no explicit `state="error"` rendered the helper text and styled itself
 * `default`, silently swallowing the message. `deriveInputState` resolves the state FROM the message, so a message that
 * renders nothing is no longer expressible.
 */
function useInputSetup(opts: {
	id?: string;
	state?: FormFieldState;
	fullWidth: boolean;
	/** The field's size, which its helper line follows (`FormFieldStylesConfig.size`). */
	size?: ComponentSize;
	errorMessage?: string;
	successMessage?: string;
	helperText?: string;
}) {
	const { isFocused, setIsFocused, fieldId: inputId, helperId } = useFormFieldState(opts.id);
	const state = deriveInputState(opts.errorMessage, opts.successMessage, opts.state);
	const stateStyle = inputStateStyles[state];
	const formFieldStyles = useFormFieldStyles({
		fullWidth: opts.fullWidth,
		state,
		size: opts.size,
	});
	const displayHelperText = deriveInputMessage(opts.errorMessage, opts.successMessage, opts.helperText);
	return {
		isFocused,
		setIsFocused,
		inputId,
		helperId,
		state,
		stateStyle,
		formFieldStyles,
		displayHelperText,
	};
}

/**
 * The footer's character count, on `Textarea`'s own contract (2026-08-21, the groups name/description caps): a
 * controlled `value` is authoritative and its length is read during render; an uncontrolled input tracks its own count
 * through a wrapped `onChange`. The wrap is applied only when the count is SHOWN, so an input that never asked for a
 * counter keeps the caller's handler identity untouched.
 */
function useInputCharCount(opts: {
	showCount: boolean;
	value: InputProps['value'];
	defaultValue: InputProps['defaultValue'];
	onChange: React.ChangeEventHandler<HTMLInputElement> | undefined;
}) {
	const { showCount, value, defaultValue, onChange } = opts;
	const [uncontrolledCount, setUncontrolledCount] = React.useState(() => coerceString(defaultValue).length);
	const countingOnChange = React.useCallback<React.ChangeEventHandler<HTMLInputElement>>(
		(event) => {
			setUncontrolledCount(event.target.value.length);
			onChange?.(event);
		},
		[onChange],
	);
	return {
		charCount: value !== undefined ? String(value).length : uncontrolledCount,
		onChange: showCount ? countingOnChange : onChange,
	};
}

function destructureInputProps(inputProps: InputProps) {
	const {
		size = 'md',
		// NO `= 'default'` DEFAULT ANY MORE: the absence of an explicit state is what lets `errorMessage` decide one,
		// and defaulting here would have made every input look explicitly-default to the shared derivation.
		state,
		variant = 'default',
		label,
		helperText,
		errorMessage,
		successMessage,
		leftAddon,
		rightAddon,
		fullWidth = false,
		showCount = false,
		// Defaulted ONCE at the boundary, exactly as `showCount` is: both variant content components take a resolved
		// boolean, so neither has to re-decide what an omitted `bare` means.
		bare = false,
		disabled,
		className = '',
		wrapperClassName = '',
		style,
		containerStyle,
		id,
		required,
		value,
		defaultValue,
		...props
	} = inputProps;
	return {
		size,
		state,
		variant,
		label,
		helperText,
		errorMessage,
		successMessage,
		leftAddon,
		rightAddon,
		fullWidth,
		showCount,
		bare,
		disabled,
		className,
		wrapperClassName,
		style,
		containerStyle,
		id,
		required,
		value,
		defaultValue,
		props,
	};
}

function InputRender(inputProps: InputProps, ref: React.ForwardedRef<HTMLInputElement>) {
	const p = destructureInputProps(inputProps);
	const { isFocused, setIsFocused, inputId, helperId, state, stateStyle, formFieldStyles, displayHelperText } = useInputSetup({
		id: p.id,
		state: p.state,
		fullWidth: p.fullWidth,
		size: p.size,
		errorMessage: p.errorMessage,
		successMessage: p.successMessage,
		helperText: p.helperText,
	});
	const { charCount, onChange } = useInputCharCount({
		showCount: p.showCount,
		value: p.value,
		defaultValue: p.defaultValue,
		onChange: p.props.onChange,
	});

	const shared = {
		inputRef: ref,
		inputId,
		helperId,
		label: p.label,
		displayHelperText,
		// The DERIVED state, not the raw prop: the two differ exactly when a message was passed without one.
		state,
		stateStyle,
		isFocused,
		setIsFocused,
		disabled: p.disabled,
		fullWidth: p.fullWidth,
		className: p.className,
		wrapperClassName: p.wrapperClassName,
		style: p.style,
		containerStyle: p.containerStyle,
		required: p.required,
		value: p.value,
		defaultValue: p.defaultValue,
		showCount: p.showCount,
		bare: p.bare,
		charCount,
		// `maxLength` rides the native-attribute rest, where the element reads it; the footer reads the same member.
		maxLength: p.props.maxLength,
		props: { ...p.props, onChange },
	};

	if (p.variant === 'floating') {
		return <FloatingInputContent {...shared} />;
	}

	return (
		<DefaultInputContent {...shared} size={p.size} leftAddon={p.leftAddon} rightAddon={p.rightAddon} formFieldStyles={formFieldStyles} />
	);
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(InputRender);

Input.displayName = 'Input';
